<template>
  <section class="my-profile position-relative">
    <PageHeader showBreadcrumbs :page-title="userName" wrapperClassName="title-container__my-profile"
      :pageLoading="isDataLoading">
      <template v-if="userName" #onSideOfTitle>
        <template v-if="isUserProfile">
          <div class="d-flex align-items-center cursor-pointer">
            <span
              v-b-tooltip.hover="{ variant: 'base', title: 'User profile will only appear on the available profiles page. It can later be converted into a regular user if needed.' }"
              class="material-icons">
              info
            </span>
          </div>
          <UserStatus :status="'active'" :user="table.items" />
        </template>
        <template v-if="!isDataLoading && table.items.status">
          <UserStatus :status="table.items.status" :invitation="table.items.actual_invitation"
            @invitationAction="() => { console.log('onInvitationStatusClick') }" :user="table.items" />
        </template>
      </template>
      <template #buttons>
        <template v-if="isUserInvited && !isDataLoading">
          <b-button class="button title-container__controls-item" variant="secondary"
            @click='resendInvitation(table.items.actual_invitation.id)'>
            Resend invitation
          </b-button>
          <b-button class="button title-container__controls-item" variant="danger"
            @click='deleteInvitation(table.items.actual_invitation.id)'>
            Revoke invitation
          </b-button>
        </template>
        <b-button v-if="$can('delete-users') && !isUserProfile"
          class="button title-container__controls-item border-unset d-none" variant="outline-danger"
          @click.prevent="deleteProfileHandler">
          Delete profile
        </b-button>
        <b-button v-if="$can('update_all-users') && isUserProfile" class="button title-container__controls-item"
          variant="secondary" @click.prevent="editProfile.showConvertIntoUserModal = true">
          Convert
        </b-button>
        <b-button v-if="!isUserInvited && !isUserProfile && $can('update_all-users')"
          class="button title-container__controls-item" variant="secondary" @click="setVisiblePasswordModal(true)">
          Change password
        </b-button>
        <b-button v-if="$can('update_all-users')" class="button" variant="primary" :disabled="isDataLoading"
          @click="onEditProfileClick">
          Edit profile
        </b-button>
      </template>
    </PageHeader>
    <div class="parent-wrapper">
      <div class="profile-avatar content-wrapper  profile-avatar-wrapper d-flex flex-column align-items-center">
        <b-img v-if="userAvatar" :src="userAvatar" rounded="circle" alt="Circle image" class="user-avatar" />
        <b-img v-else v-bind="mainProps" rounded="circle" alt="Circle image" class="user-avatar" />
        <p class="name">{{ userName }}</p>
        <p class="position">{{ userPosition }}</p>
      </div>
      <div class="profile-skills" :class="{ 'profile-skills-without-comments': !$can(permissions.ReadAllComments) }">
        <UserSkills :isUserProfile="isUserProfile" />
      </div>
      <div class="profile-information">
        <ProfileInformationTable :items="tableData" :fields="table.informationTableFields" />
      </div>
      <div class="profile-personal">
        <ProfileInformationTable :items="tableData" :fields="table.personalInformationTableFields"
          title="Contact information" :isEditAllowed="isUserInvited" />
      </div>
      <div class="profile-visibility">
        <div class="content-wrapper h-100 information-wrapper">
          <div class="block-title">Visibility & reporting</div>
          <b-form-group v-for="field in Object.keys(visibilityForm)" v-bind:key="field"
            class="visibility-form form-email-notifications">
            <div v-if="!visibilityForm[field].hidden" class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="visibility-label">{{ visibilityForm[field].label }} </div>
                <div class="visibility-description mt-2">{{ visibilityForm[field].description }}<a
                    :href="visibilityForm[field].link" target="_blank">{{ visibilityForm[field].linkText }}</a>.</div>
              </div>
              <b-button variant="white" class="button btn-icon button-checkbox_sm">
                <b-form-checkbox size="sm" :disabled="visibilityForm[field].disabled"
                  @change="(value) => updateVisibilityFormFields(field, value)"
                  :id="`${visibilityForm[field].name}-switch`" switch v-model="visibilityForm[field].value"
                  class="switch" />
              </b-button>
            </div>
          </b-form-group>
        </div>
      </div>
      <div class="profile-comment" v-if="$can(permissions.ReadAllComments)">
        <div class="content-wrapper h-100 information-wrapper">
          <TextEditor :in-modal="false" show-title :entity-type="commentEntityTypes.UserListComment"
            :entity-id="userId" />
        </div>
      </div>
    </div>

    <ModalRestoreUser v-model="modalRestoreUser.show" :loading="isDataLoading" @submit="restoreUser" />
    <ModalDeleteUser v-model="modalDeletedUser.show" :loading="isDataLoading" @submit="deletedUser" />
    <ChangePasswordModal
      v-model="changePassword.showModal"
      :showCurrentPasswordField="false"
      :onSubmit="handlePasswordSubmit"
      :loading="isDataLoading"
    />

    <ModalConvertIntoUser v-model="editProfile.showConvertIntoUserModal" :user-id="userId" />

    <SlideOutContainerViaPortal :loading="isEditProfileLoading">
      <div v-if="showEdit">
        <EditProfile :userId="userId" @close="onClose" :isUserProfile="isUserProfile" :userData="tableDataEdit"
          @profileUpdate="onProfileUpdate" @avatarUpdate="onAvatarUpdate" @loadingChange="handleEditProfileLoadingChange" :isSliderDataLoading="isEditProfileLoading" isShowStickyButtonsAnimation :breadcrumbs="[{
            text: 'Home',
            to: { name: 'home' }
          }, { text: userName, click: () => onClose() }]" />
      </div>
    </SlideOutContainerViaPortal>
  </section>

</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import UserSkills from './UserSkills'
import ModalRestoreUser from '@components/modals/ModalRestoreUser'
import ModalDeleteUser from '@components/modals/ModalDeleteUser'
import ModalConvertIntoUser from '@components/modals/ModalConvertIntoUser'
import TextEditor from '@components/TextEditor.vue'
import EditProfile from '@components/EditProfile'
import UserMixin from '@mixins/UserMixin'
import ProfileInformationTable from '@components/tables/ProfileInformationTable'
import { commentEntityTypes } from '@mocks/comment-entity-types'
import PageHeader from '@components/PageHeader.vue'
import UserStatus from '@/components/UsersInfo/UserStatus'
import { validationMixin } from 'vuelidate'
import { email } from 'vuelidate/lib/validators'
import { permissions } from '@mocks/permissions'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import ChangePasswordModal from '@components/common/ChangePasswordModal.vue'

export default {
  name: 'Profile',
  mixins: [UserMixin, validationMixin],
  components: {
    UserSkills,
    ModalRestoreUser,
    ModalDeleteUser,
    ModalConvertIntoUser,
    TextEditor,
    ProfileInformationTable,
    PageHeader,
    UserStatus,
    SlideOutContainerViaPortal,
    EditProfile,
    ChangePasswordModal
  },
  computed: {
    ...mapGetters({
      getAllPartners: 'partners/getAllPartners'
    }),

    tableData () {
      const data = this.table.items
      const contactData = data.contacts
      return { ...contactData, ...data }
    },
    tableDataEdit () {
      return this.table.items
    },
    commentEntityTypes () {
      return commentEntityTypes
    },
    isUserInvited () {
      if (this.table.items.type === 'user-profile') {
        return false
      }

      if (this.table.items.actual_invitation?.accepted_at) {
        return false
      }

      return Boolean(this.table.items.actual_invitation)
    }

  },
  data () {
    return {
      permissions,
      userId: null,
      userType: null,
      userName: '',
      userAvatar: '',
      userPosition: '',
      isUserProfile: false,
      mainProps: { width: 75, height: 75, class: 'm1', src: '/images/avatar-placeholder.png' },
      readonly: true,
      table: {
        items: {},
        informationTableFields: [
          {
            key: 'payment_type',
            label: 'Payment type'
          },
          {
            key: 'employment_type',
            label: 'Employment type',
            isObject: 'name',
            class: 'text-lowercase'
          },
          {
            key: 'allocations',
            label: 'Allocation',
            isArray: true,
            isObject: 'name'
          },
          {
            key: 'availability',
            label: 'Availability',
            isObject: 'name'
          },
          {
            key: 'years_of_experience',
            label: 'Experience'
          },
          {
            key: 'seniority_lvl',
            label: 'Seniority level',
            isObject: 'name'
          },
          {
            key: 'english_lvl',
            label: 'English level',
            isObject: 'name'
          },
          {
            key: 'roles',
            label: 'Role',
            isArray: true,
            isObject: 'display_name'
          },
          {
            key: 'cv_link',
            label: 'CV',
            isLink: true
          }
        ],
        personalInformationTableFields: [
          {
            key: 'skype',
            label: 'Skype',
            icon: 'skype'
          },
          {
            key: 'telegram',
            label: 'Telegram',
            icon: 'telegram'
          },
          {
            key: 'email',
            label: 'Email',
            icon: 'email',
            isEditable: true,
            sub: (newEmail, oldEmail) => { this.updateEmail(newEmail, oldEmail) },
            validation: (value) => {
              if (value) {
                return email(value)
              } else {
                return true
              }
            },
            info: "This user hasn't yet accepted the invitation.<br>If you edit the email address, the invitation will be revoked and a new one will be sent to the updated email address."
          },
          {
            key: 'mobile',
            label: 'Phone',
            icon: 'phone'
          },
          {
            key: 'other',
            label: 'Other',
            icon: 'calendar'
          }
        ]
      },
      modalRestoreUser: {
        show: false,
        user: null
      },
      modalDeletedUser: {
        show: false,
        user: null
      },
      changePassword: {
        showModal: false
      },
      visibilityForm: {
        public: {
          type: 'switch',
          name: 'public',
          label: 'Public',
          value: null,
          disabled: !this.$can('update_all-users'),
          class: 'col-12',
          description: 'Enabling this will make the user visible on your company’s public page with ',
          linkText: 'available profiles',
          link: '/available-profiles'
        },
        contact: {
          type: 'switch',
          name: 'contact',
          label: 'Contact',
          value: null,
          class: 'col-12',
          disabled: !this.$can('update_all-users'),
          description: 'Enabling this will make the user visible in the contacts section of your company’s public page along with ',
          linkText: 'available profiles',
          link: '/available-profiles'
        },
        reporting: {
          type: 'switch',
          name: 'reporting',
          label: 'Reporting',
          value: null,
          class: 'col-12',
          hidden: this.isUserProfile,
          disabled: !this.$can('update_all-users'),
          description: 'This option allows you to configure whether the user is required to report their hours in the ',
          linkText: 'working time report',
          link: '/working-time'
        },
        active_user: {
          type: 'switch',
          name: 'active_user',
          label: 'Active user',
          value: false,
          class: 'col-12',
          hidden: this.isUserProfile,
          disabled: !this.$can('update_all-users'),
          description: 'Allows you to configure whether the user should remain active in the system'
        }
      },
      isAvatarUploading: false,
      isEditProfileLoading: false,
      editParam: false,
      showEdit: false

    }
  },
  methods: {
    ...mapActions({
      setComment: 'comments/setComment',
      setLoadedUser: 'users/setLoadedUser'
    }),
    async handlePasswordSubmit (data) {
      const updatedSuccessfully = await this.updatePasswordRequest(data)
      if (updatedSuccessfully) {
        this.setVisiblePasswordModal(false)
      }
    },
    setVisiblePasswordModal (status) {
      this.changePassword.showModal = status
    },
    changeUserStatus (user, value) {
      value ? this.showModalRestoreUser(user) : this.showModalDeletedUser(user)
    },
    async updatePasswordRequest (data) {
      this.isLoading = true
      return this.axios.put(
        `/api/user/update-password/${this.userId}`,
        data,
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }).then(response => {
        if (response.status === 200) {
          this.$snotify.success('Password succesfully updated', 'Success', this.$store.state.snotify)
        }
        return true
      }).catch(err => {
        if (err.response.data.errors) {
          this.errorNotifications(err)
          return false
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    deletedUser (data) {
      // eslint-disable-next-line camelcase
      const date_exit = this._toLocalTime(data.date.value)

      this._toggleDataPageLoading(() => {
        return this.$api.users.deleteUser(this.modalDeletedUser.user.id, { date_exit })
          .then(response => {
            this.modalDeletedUser.user.deleted_at = response.deleted_at
            this.modalDeletedUser.user.exit_at = response.exit_at
            this.visibilityForm.active_user.value = false
            this.setTable(response)
          })
          .catch(err => {
            this.$snotify.error(
              err.response.data.message,
              'Error',
              this.$store.state.snotify
            )
          })
          .finally(() => {
            this.hideModalDeletedUser()
          })
      })
    },
    showModalRestoreUser (user) {
      this.modalRestoreUser.show = true
      this.modalRestoreUser.user = user
    },
    hideRestoreUser () {
      this.modalRestoreUser.show = false
      this.modalRestoreUser.user = null
    },
    showModalDeletedUser (user) {
      this.modalDeletedUser.show = true
      this.modalDeletedUser.user = user
    },
    hideModalDeletedUser () {
      this.modalDeletedUser.show = false
      this.modalDeletedUser.user = null
    },
    setTable (res) {
      const data = { ...res }
      data.userStatus = data.deleted_at === null && data.exit_at === null && data.status === 'approved'
      data.no_reporting = !!data.no_reporting
      data.employment_type_full_label = data.employment_type.label
        ? data.employment_type.label + (data.partner && data.employment_type.type === 'partner' ? ` (${data.partner.company_name})` : '')
        : data.employment_type.name
      data.availability = data.availability && data.availability.name ? data.availability.name : ''
      data.seniority_lvl = data.seniority_lvl && data.seniority_lvl.name ? data.seniority_lvl.name : ''
      data.english_lvl = data.english_lvl && data.english_lvl.name ? data.english_lvl.name : ''
      this.visibilityForm.public.value = !!data.show_on_widget
      this.visibilityForm.contact.value = !!data.is_contact_person
      this.visibilityForm.reporting.value = !data.no_reporting
      this.visibilityForm.active_user.value = !data.exit_at || false
    },
    restoreUser (data) {
      // eslint-disable-next-line camelcase
      const date_start = this._toLocalTime(data.date.value)

      this._toggleDataPageLoading(() => {
        return this.$api.users.restoreUser(this.modalRestoreUser.user.id, { date_start })
          .then((res) => {
            this.modalRestoreUser.user.deleted_at = res.deleted_at
            this.modalRestoreUser.user.exit_at = res.exit_at
            this.visibilityForm.active_user.value = true
            this.setTable(res)
          }).catch(err => {
            this.$snotify.error(
              err.response.data.message,
              'Error',
              this.$store.state.snotify
            )
          })
          .finally(() => {
            this.hideRestoreUser()
          })
      })
    },
    switchNoReporting (userId, fieldName, value) {
      const invertedNoReporting = value ? 0 : 1
      this.axios.put(
        `/api/switch-no-reporting/${userId}/${invertedNoReporting}`,
        {},
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }).then(response => {
        if (response.status === 200) {
          this.visibilityForm[fieldName].value = value

          this.$snotify.success('No reporting ' + (response.data.no_reporting ? 'On' : 'Off'), 'Success', this.$store.state.snotify)
        }
      }).catch(err => {
        this.errorNotifications(err)
      })
    },
    switchShowOnProfilesWidget (userId, fieldName, value) {
      this.axios.put(
        `/api/users/${userId}/show-on-profiles-widget`,
        {
          userType: this.userType,
          showOnWidgetStatus: value ? 1 : 0
        },
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }).then(response => {
        if (response.status === 200) {
          this.visibilityForm[fieldName].value = value
          if (this.table && this.table.items && this.table.items[0]) {
            this.table.items[0].show_on_widget = value
          }
          this.$snotify.success('Show on Profiles Widget ' + (value ? 'On' : 'Off'), 'Success', this.$store.state.snotify)
        }
      }).catch(err => {
        this.errorNotifications(err)
      })
    },
    switchIsContactPerson (userId, fieldName, value) {
      this.axios.put(
        this.isUserProfile ? `/api/users/profiles/${userId}/contact-person` : `/api/users/${userId}/contact-person`,
        {
          isContactPerson: value ? 1 : 0
        },
        {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        }).then(response => {
        if (response.status === 200) {
          this.visibilityForm[fieldName].value = value
          if (this.table && this.table.items && this.table.items[0]) {
            this.table.items[0].is_contact_person = value
          }
          this.$snotify.success('Contact Person ' + (value ? 'On' : 'Off'), 'Success', this.$store.state.snotify)
        }
      }).catch(err => {
        this.errorNotifications(err)
      })
    },
    async update (data) {
      this.isDataLoading = true
      try {
        const isUpdatesUser = !this.isUserProfile
        const response = isUpdatesUser ? await this.updateUser(data) : await this.updateProfile(data)
        if (response.status === 200) {
          this.$snotify.success('Profile successfully updated', 'Success', this.$store.state.snotify)
        }
        data = response.data
        data.show_on_widget = data.show_on_widget === 1 || data.show_on_widget === true
        data.is_contact_person = data.is_contact_person === 1 || data.is_contact_person === true
        this.userName = data.name
        this.userAvatar = data.avatar_url
        this.userPosition = data.position
        this.setTable(data)
      } catch (err) {
        this.errorNotifications(err)
      } finally {
        this.isDataLoading = false
      }
    },
    uploadCroppedAvatar () {
      const { canvas } = this.$refs.avatarCropper.getResult()

      if (canvas) {
        const form = new FormData()
        canvas.toBlob(blob => {
          form.append('file', blob)
          const requestUrl = this.isUserProfile
            ? `/api/users/profiles/${this.userId}/update-avatar`
            : `/api/users/${this.userId}/update-avatar`
          this.isAvatarUploading = true
          this.axios.post(
            requestUrl,
            form,
            {
              headers: {
                Authorization: 'Bearer' + localStorage.getItem('access_token')
              }
            }).then(response => {
            if (response.status === 200) {
              this.$snotify.success('Profile successfully updated', 'Success', this.$store.state.snotify)
            }
            this.userAvatar = response.data.avatar_url
          }).catch(err => {
            this.errorNotifications(err)
          }).finally(() => {
            this.isAvatarUploading = false
            this.editProfile.showAvatarCropModal = false
          })
        }, 'image/jpeg')
      }
    },
    updateVisibilityFormFields (name, value) {
      switch (name) {
        case 'public':
          this.switchShowOnProfilesWidget(this.table.items.id, name, value)
          break
        case 'contact':
          this.switchIsContactPerson(this.table.items.id, name, value)
          break
        case 'reporting':
          this.switchNoReporting(this.table.items.id, name, value)
          break
        case 'active_user':
          this.visibilityForm.active_user.value = !value
          this.changeUserStatus(this.table.items, value)
          break
        default:
          break
      }
    },
    resendInvitation (id) {
      this.axios
        .put('/api/user-invitation/resend/' + id,
          {},
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          })
        .then(({ data }) => {
          this.$snotify.success(
            data.statusText,
            'Success',
            this.$store.state.snotify
          )
        })
        .catch(error => {
          this.errorNotifications(error)
        })
    },
    async deleteInvitation (id) {
      const confirm = await this.$root.$confirm.open(
        'Confirm',
        'Are you sure to revoke the invitation to ' + this.table.items.actual_invitation.email +
        '? This will invalidate the invite link and erase all the data about this user’s profile from the portal.'
      )

      if (!confirm) return

      this.axios
        .delete('/api/user-invitation/delete/' + id,
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          })
        .then(async ({ data }) => {
          this.$snotify.success(
            data.statusText,
            'Success',
            this.$store.state.snotify
          )
          await this.setComment({ type: commentEntityTypes.UserListComment, id: this.table.items.id, comment: '' })
          this.$router.push({ name: this.ROUTES.HOME.name })
        })
        .catch(error => {
          this.errorNotifications(error)
        })
    },
    async updateEmail (email, oldEmail) {
      this.axios
        .put('/api/user-invitation/update-email/' + this.table.items.actual_invitation.id,
          { email },
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          })
        .then(({ data }) => {
          this.$snotify.success(
            data.statusText,
            'Success',
            this.$store.state.snotify
          )
        })
        .catch(error => {
          this.table.items.email = oldEmail
          this.errorNotifications(error)
        })
    },
    async deleteProfileHandler () {
      const confirm = await this.$root.$confirm.open(
        'Delete Profile',
        'Are you sure you want to delete the user profile?',
        { submitText: 'Delete' }
      )

      if (!confirm) return

      return this.$api.users.deleteUserProfile(this.userId).then((text) => {
        this.$snotify.success(text, 'Success', this.$store.state.snotify)
        this.$router.push({ name: 'home' })
      }).catch(err => {
        this.errorNotifications(err)
      })
    },
    onEditProfileClick (e) {
      e.target.blur()
      this.fillEditUserForm(this.table.items)
      const route = this.$router.resolve({
        name: 'profile-edit',
        params: { id: this.userId, type: this.userType }
      })

      window.history.pushState({}, '', route.href)
      this.showEdit = true
    },
    onClose () {
      const route = this.$router.resolve({ name: 'profile', params: { id: this.userId, type: this.userType } })
      window.history.pushState({}, '', route.href)
      this.showEdit = false
    },
    onProfileUpdate (data) {
      this.userName = data.name
      this.userAvatar = data.avatar_url
      this.userPosition = data.position

      this.table.items = data
      this.setTable(data)
      this.onClose()
    },
    onAvatarUpdate (avatar) {
      this.userAvatar = avatar
    },
    handleEditProfileLoadingChange (loading) {
      this.isEditProfileLoading = loading
    }

  },
  created () {
    this.userId = this.$route.params.id
    this.userType = this.$route.params.type
    this.isUserProfile = this.userType === 'user-profile'

    this.visibilityForm.reporting.hidden = this.isUserProfile
    this.visibilityForm.active_user.hidden = this.isUserProfile
  },
  async mounted () {
    this.isDataLoading = true
    try {
      const response = this.isUserProfile ? await this.fetchUserProfile(this.userId) : await this.fetchUser(this.userId)
      const data = response.data
      this.setLoadedUser(data)
      data.show_on_widget = data.show_on_widget === 1 || data.show_on_widget === true
      data.is_contact_person = data.is_contact_person === 1 || data.is_contact_person === true
      this.readonly = false

      this.userName = data.name
      this.userAvatar = data.avatar_url
      this.userPosition = data.position

      this.table.items = data

      this.setTable(data)
    } catch (err) {
      if (err.response.status === 404) {
        this.$router.push('/404')
      }
    } finally {
      this.isDataLoading = false
    }
  }

}
</script>

<style scoped lang="scss">

.visibility-form {
  margin-top: 10px;
  margin-bottom: unset;
  .visibility-label {
    font-weight: 500;
    font-size: 13px;
    line-height: 24px;
  }
  .visibility-description {
    font-weight: 400;
    font-size: 11px;
    line-height: 20px;
  }
}

.parent-wrapper {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(4, auto);
    gap: 30px;
    // & div {
      // align-self: flex-start;
    // }
}

.profile-avatar {
    grid-row: span 2 / span 3;
}

.profile-information {
    grid-row: span 2 / span 4;
}

.profile-skills {
    grid-column-start: 1;
    grid-row-start: 3;
}
.profile-skills-without-comments {
  grid-row: 3 / span 2;
}

.profile-personal {
    grid-row: span 2 / span 2;
    grid-column-start: 3;
    grid-row-start: 1;
}

.profile-visibility {
    grid-row: span 2 / span 2;
    grid-column-start: 2;
    grid-row-start: 3;
    grid-column-end: 4;
    align-self: flex-start;
}

.profile-comment {
  grid-column-start: 1;
  grid-row-start: 4;
  .editor-container {
    height: 100%;
  }
}

.user-profile-badge {
  font-size: 14px;
  vertical-align: middle;
  margin-left: 10px;
}

  .content-wrapper {
    background-color: #ffffff;
    border-radius: 10px;
  }

  .profile-avatar-wrapper {
    padding: 30px;
  }

  .information-wrapper {
    padding: 20px;
  }

  .user-avatar {
    width: 200px;
    height: 200px;
  }

  .info-item {
    margin-top: 10px;
  }

// Component-specific styles (password form styles moved to _password-forms.scss)

</style>
